import React from "react";
import {
  Box,
  Typography,
  LinearProgress,
  Stack,
  alpha,
  useTheme,
} from "@mui/material";
import { Tableau, Participation, Matchs, Tour } from "../../../types";

export interface TableauProgressBlockProps {
  tableau: Tableau;
  participations: Participation[];
  matchs: Matchs[];
  tours: Tour[];
}

type PhaseStatus = "a_venir" | "en_cours" | "termine";

function computePoules(nbPointes: number): {
  nbPoules: number;
  nbPoules3: number;
  nbPoules2: number;
  poules: Array<{ nbJoueurs: number; matchs: number }>;
} {
  if (nbPointes <= 0)
    return { nbPoules: 0, nbPoules3: 0, nbPoules2: 0, poules: [] };
  if (nbPointes === 2)
    return {
      nbPoules: 1,
      nbPoules3: 0,
      nbPoules2: 1,
      poules: [{ nbJoueurs: 2, matchs: 1 }],
    };

  let nbPoules3 = Math.floor(nbPointes / 3);
  const reste = nbPointes % 3;
  let nbPoules2 = 0;
  if (reste === 1 && nbPoules3 > 0) {
    nbPoules3 -= 1;
    nbPoules2 = 2;
  } else {
    nbPoules2 = reste;
  }

  const poules = [
    ...Array(nbPoules3).fill({ nbJoueurs: 3, matchs: 3 }),
    ...Array(nbPoules2).fill({ nbJoueurs: 2, matchs: 1 }),
  ];

  return {
    nbPoules: nbPoules3 + nbPoules2,
    nbPoules3,
    nbPoules2,
    poules,
  };
}

function getNbPlayersForTour(tour: Tour) {
  const phase = (tour.phase || tour.label || "").toLowerCase();
  if (phase.includes("quart")) return 8;
  if (/(^|\s|-)8e/.test(phase)) return 16;
  if (phase.includes("64")) return 64;
  if (phase.includes("32")) return 32;
  if (phase.includes("16")) return 16;
  if (phase.includes("demi")) return 4;
  if (phase.includes("finale")) return 2;
  return 0;
}

function phaseStatus(
  termines: number,
  total: number,
  enCours: number
): PhaseStatus {
  if (total > 0 && termines >= total && enCours === 0) return "termine";
  if (termines > 0 || enCours > 0) return "en_cours";
  return "a_venir";
}

function progressPct(termines: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.round((termines / total) * 100));
}

function statusLabel(status: PhaseStatus): string {
  switch (status) {
    case "termine":
      return "Terminé";
    case "en_cours":
      return "En cours";
    default:
      return "À venir";
  }
}

const TableauProgressBlock: React.FC<TableauProgressBlockProps> = ({
  tableau,
  participations,
  matchs,
  tours,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const nbMax = tableau.nbPlace > 0 ? tableau.nbPlace : null;
  const nbInscrits = participations.length;
  const nbPointes = participations.filter((p) => p.checkedIn).length;

  const { nbPoules, nbPoules3, nbPoules2, poules } = computePoules(nbPointes);
  const pouleMatchs = matchs.filter((m) => m.tour?.type === "poules");

  let pouleMatchOffset = 0;
  const matchsParPoule = poules.map((poule) => {
    const matchsPoule = pouleMatchs.slice(
      pouleMatchOffset,
      pouleMatchOffset + poule.matchs
    );
    pouleMatchOffset += poule.matchs;
    const termines = matchsPoule.filter((m) => m.status === "TERMINE").length;
    return { ...poule, termines };
  });

  const poulesTerminees = matchsParPoule.filter(
    (p) => p.termines === p.matchs
  ).length;

  // Dénominateur poules : attendu (pointés) ou, à défaut, matchs observés
  const expectedPouleMatchs = poules.reduce((sum, p) => sum + p.matchs, 0);
  const matchsPoulesTermines = pouleMatchs.filter(
    (m) => m.status === "TERMINE"
  ).length;
  const matchsPoulesEnCours = pouleMatchs.filter(
    (m) => m.status === "ENCOURS"
  ).length;
  const totalMatchsPoules = Math.max(expectedPouleMatchs, pouleMatchs.length);
  const statusPoules = phaseStatus(
    matchsPoulesTermines,
    totalMatchsPoules,
    matchsPoulesEnCours
  );

  let nbQualifies = 0;
  if (nbPointes > 0 && nbPoules > 0) {
    nbQualifies = nbPoules * 2;
  }
  let nbExempts = 0;
  if (nbQualifies > 2) {
    nbExempts = Math.max(0, 8 - nbQualifies);
  }

  const toursElimSorted = tours
    .filter((t) => t.type === "elim")
    .sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0));

  const startTour =
    [...toursElimSorted]
      .sort((a, b) => getNbPlayersForTour(a) - getNbPlayersForTour(b))
      .find((t) => getNbPlayersForTour(t) >= nbQualifies) || toursElimSorted[0];
  const startIdx = startTour
    ? toursElimSorted.findIndex((t) => t.id === startTour.id)
    : -1;

  type ElimTourRow = {
    id: string;
    label: string;
    total: number;
    termines: number;
    enAttente: number;
    enCours: number;
    status: PhaseStatus;
  };

  const toursElim: ElimTourRow[] = [];

  if (startIdx >= 0) {
    let joueursRestants = Math.max(0, nbQualifies - nbExempts);
    let exemptsRestants = nbExempts;

    for (let i = startIdx; i < toursElimSorted.length; i++) {
      const tour = toursElimSorted[i];
      const tourMatchs = matchs.filter((m) => m.tourId === tour.id);
      const termines = tourMatchs.filter((m) => m.status === "TERMINE").length;
      const enAttente = tourMatchs.filter(
        (m) => m.status === "CREE" || m.status === "LIBERE"
      ).length;
      const enCours = tourMatchs.filter((m) => m.status === "ENCOURS").length;

      let expected = Math.floor(joueursRestants / 2);
      // Si pas de pointage mais des matchs réels : se baser sur l’observé
      const total = Math.max(expected, tourMatchs.length);

      if (total <= 0 && i !== toursElimSorted.length - 1) break;

      // N’afficher un tour vide que s’il est le suivant logique attendu
      if (total <= 0 && tourMatchs.length === 0) {
        if (nbQualifies === 0) break;
      }

      toursElim.push({
        id: String(tour.id),
        label: tour.label,
        total,
        termines,
        enAttente,
        enCours,
        status: phaseStatus(termines, total, enCours),
      });

      if (i === startIdx) {
        joueursRestants = Math.max(expected, 0) + exemptsRestants;
        exemptsRestants = 0;
      } else {
        joueursRestants = Math.max(expected, 0);
      }
      if (expected === 1 || total === 1) break;
    }
  }

  // Fallback : tours élim avec matchs observés même sans pointés
  if (toursElim.length === 0) {
    for (const tour of toursElimSorted) {
      const tourMatchs = matchs.filter((m) => m.tourId === tour.id);
      if (tourMatchs.length === 0) continue;
      const termines = tourMatchs.filter((m) => m.status === "TERMINE").length;
      const enAttente = tourMatchs.filter(
        (m) => m.status === "CREE" || m.status === "LIBERE"
      ).length;
      const enCours = tourMatchs.filter((m) => m.status === "ENCOURS").length;
      toursElim.push({
        id: String(tour.id),
        label: tour.label,
        total: tourMatchs.length,
        termines,
        enAttente,
        enCours,
        status: phaseStatus(termines, tourMatchs.length, enCours),
      });
    }
  }

  let statusGlobal: PhaseStatus = "a_venir";
  if (
    statusPoules === "termine" &&
    toursElim.length > 0 &&
    toursElim.every((t) => t.status === "termine")
  ) {
    statusGlobal = "termine";
  } else if (
    statusPoules === "en_cours" ||
    statusPoules === "termine" ||
    toursElim.some((t) => t.status === "en_cours" || t.status === "termine")
  ) {
    statusGlobal = "en_cours";
  }

  const accent =
    statusGlobal === "termine"
      ? theme.palette.success.main
      : statusGlobal === "en_cours"
        ? theme.palette.primary.main
        : theme.palette.text.disabled;

  const surface = isDark ? alpha("#fff", 0.04) : "#FFFFFF";
  const muted = isDark ? alpha("#fff", 0.06) : alpha("#0B1F33", 0.04);

  const fillRatio =
    nbMax && nbMax > 0 ? Math.min(1, nbInscrits / nbMax) : 0;

  return (
    <Box
      sx={{
        height: "100%",
        borderRadius: 3,
        bgcolor: surface,
        border: 1,
        borderColor: isDark ? alpha("#fff", 0.1) : alpha("#0B1F33", 0.08),
        boxShadow: isDark
          ? `0 8px 24px ${alpha("#000", 0.35)}`
          : `0 8px 28px ${alpha("#0B1F33", 0.06)}`,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: isDark
            ? `0 12px 32px ${alpha("#000", 0.45)}`
            : `0 12px 32px ${alpha("#0B1F33", 0.1)}`,
        },
      }}
    >
      <Box
        sx={{
          height: 3,
          bgcolor: accent,
          opacity: statusGlobal === "a_venir" ? 0.35 : 1,
        }}
      />

      <Box sx={{ p: 2.25, flex: 1, display: "flex", flexDirection: "column" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
          sx={{ mb: 1.25 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                letterSpacing: -0.3,
                lineHeight: 1.2,
                fontSize: "1.05rem",
              }}
              noWrap
              title={`${tableau.name} — ${tableau.label}`}
            >
              {tableau.name}
              <Typography
                component="span"
                sx={{
                  fontWeight: 500,
                  color: "text.secondary",
                  ml: 0.75,
                  fontSize: "0.85rem",
                }}
              >
                {tableau.label}
              </Typography>
            </Typography>
            {tableau.heureLimite && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.35 }}
              >
                Pointage · {tableau.heureLimite}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              px: 1,
              py: 0.35,
              borderRadius: 999,
              bgcolor: alpha(accent, isDark ? 0.2 : 0.12),
              color: accent,
              fontSize: 11,
              fontWeight: 700,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {statusLabel(statusGlobal)}
          </Box>
        </Stack>

        {/* Remplissage inscriptions */}
        <Box sx={{ mb: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ mb: 0.5 }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Inscrits
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
            >
              {nbInscrits}
              {nbMax != null ? ` / ${nbMax}` : ""}
              {" · "}
              <Box component="span" sx={{ color: "success.main" }}>
                {nbPointes} pointés
              </Box>
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={fillRatio * 100}
            sx={{
              height: 6,
              borderRadius: 999,
              bgcolor: muted,
              "& .MuiLinearProgress-bar": {
                borderRadius: 999,
                bgcolor: theme.palette.info.main,
              },
            }}
          />
        </Box>

        {/* Poules */}
        <PhaseBlock
          title="Poules"
          status={statusPoules}
          termines={matchsPoulesTermines}
          total={totalMatchsPoules}
          enCours={matchsPoulesEnCours}
          detail={
            nbPoules > 0
              ? `${poulesTerminees}/${nbPoules} poules · ${nbPoules3}×3 + ${nbPoules2}×2`
              : pouleMatchs.length > 0
                ? `${pouleMatchs.length} matchs observés`
                : "En attente de pointage"
          }
          muted={muted}
          isDark={isDark}
        />

        {/* Élimination */}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: "text.secondary",
            letterSpacing: 0.4,
            textTransform: "uppercase",
            mt: 1.75,
            mb: 1,
            display: "block",
          }}
        >
          Élimination directe
          {nbQualifies > 0
            ? ` · ${nbQualifies} qualifiés${
                nbExempts > 0 ? ` · ${nbExempts} exempts` : ""
              }`
            : ""}
        </Typography>

        {toursElim.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Pas encore de tours joués
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {toursElim.map((tour) => (
              <PhaseBlock
                key={tour.id}
                title={tour.label}
                status={tour.status}
                termines={tour.termines}
                total={tour.total}
                enCours={tour.enCours}
                detail={
                  tour.enAttente > 0 || tour.enCours > 0
                    ? [
                        tour.enCours > 0 ? `${tour.enCours} en cours` : null,
                        tour.enAttente > 0 ? `${tour.enAttente} à lancer` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")
                    : undefined
                }
                muted={muted}
                isDark={isDark}
                compact
              />
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

type PhaseBlockProps = {
  title: string;
  status: PhaseStatus;
  termines: number;
  total: number;
  enCours: number;
  detail?: string;
  muted: string;
  isDark: boolean;
  compact?: boolean;
};

const PhaseBlock: React.FC<PhaseBlockProps> = ({
  title,
  status,
  termines,
  total,
  enCours,
  detail,
  muted,
  isDark,
  compact,
}) => {
  const theme = useTheme();
  const pct = progressPct(termines, total);
  const barColor =
    status === "termine"
      ? theme.palette.success.main
      : status === "en_cours"
        ? theme.palette.primary.main
        : theme.palette.grey[isDark ? 600 : 400];

  return (
    <Box
      sx={{
        p: compact ? 1.25 : 1.5,
        borderRadius: 2,
        bgcolor: muted,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        spacing={1}
        sx={{ mb: 0.75 }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, fontSize: compact ? 13 : 14 }}
        >
          {title}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            fontVariantNumeric: "tabular-nums",
            color: "text.secondary",
          }}
        >
          {total > 0 ? `${termines} / ${total}` : "—"}
          {enCours > 0 ? ` · ${enCours} live` : ""}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: compact ? 5 : 7,
          borderRadius: 999,
          bgcolor: isDark ? alpha("#fff", 0.08) : alpha("#0B1F33", 0.08),
          "& .MuiLinearProgress-bar": {
            borderRadius: 999,
            bgcolor: barColor,
          },
        }}
      />
      {detail && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.65 }}
        >
          {detail}
        </Typography>
      )}
    </Box>
  );
};

export default TableauProgressBlock;
